import { Subject } from 'rxjs';
import { retryWhen, delay, take, filter } from 'rxjs/operators';
import { webSocket } from 'rxjs/webSocket';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * Event name formatter
 */
class EventFormatter {
    /**
     * Create a new class instance.
     */
    constructor(namespace) {
        this.setNamespace(namespace);
    }
    /**
     * Format the given event name.
     */
    format(event) {
        if (event.charAt(0) === '.' || event.charAt(0) === '\\') {
            return event.substr(1);
        }
        else if (this.namespace) {
            event = this.namespace + '.' + event;
        }
        return event.replace(/\./g, '\\');
    }
    /**
     * Set the event namespace.
     */
    setNamespace(value) {
        this.namespace = value;
    }
}

/**
 * This class represents a Socket.io channel.
 */
class Channel {
    /**
     * Create a new class instance.
     */
    constructor(webSocket, name, options = {}) {
        /**
         * The subscriptions of the channel.
         */
        this.subscriptions = {};
        this.name = name;
        this.webSocket = webSocket;
        this.options = options;
        this.eventFormatter = new EventFormatter(this.options.namespace);
    }
    /**
     * Listen for an event on the channel instance.
     */
    listen(event, callback) {
        event = this.eventFormatter.format(event);
        this.webSocket.on(this.name, event, (message) => callback(message));
        return this;
    }
    /**
      * Listen for a whisper event on the channel instance.
      */
    listenForWhisper(event, callback) {
        return this.listen('.client-' + event, callback);
    }
    /**
       * Listen for an event on the channel instance.
       */
    notification(callback) {
        return this.listen('.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated', callback);
    }
    /**
     * Stop listening for an event on the channel instance.
     */
    stopListening(event) {
        event = this.eventFormatter.format(event);
        this.webSocket.off(this.name, event);
        return this;
    }
    /**
     * Register a callback to be called anytime a subscription succeeds.
     */
    subscribed(callback) {
        // @todo: Implement this.
        return this;
    }
    /**
     * Register a callback to be called anytime an error occurs.
     */
    error(callback) {
        // @todo: Implement this.
        return this;
    }
}

/**
 * This class represents a Socket.io presence channel.
 */
class PrivateChannel extends Channel {
    /**
     * Trigger client event on the channel.
     */
    whisper(eventName, data) {
        // Todo: implement
        throw new Error("Client side events have not been implemented yet.");
    }
}

/**
 * This class represents a Socket.io presence channel.
 */
class PresenceChannel extends PrivateChannel {
    /**
     * Register a callback to be called anytime the member list changes.
     */
    here(callback) {
        this.listen("presence:subscribed", (members) => {
            callback(members.map((m) => m.user_info));
        });
        return this;
    }
    /**
     * Listen for someone joining the channel.
     */
    joining(callback) {
        this.listen("presence:joining", (member) => callback(member.user_info));
        return this;
    }
    /**
     * Listen for someone leaving the channel.
     */
    leaving(callback) {
        this.listen("presence:leaving", (member) => callback(member.user_info));
        return this;
    }
}

class WebSocketConnector {
    /**
     * Create a new instance of the connector.
     */
    constructor(webSocket, options = {}) {
        this.webSocket = webSocket;
        this.options = options;
        /**
         * All of the subscribed channel names.
         */
        this.channels = {};
        /**
         * The connected state of the connector.
         */
        this.connected = false;
    }
    /**
     * Get a channel instance by name.
     */
    channel(name) {
        if (!this.channels[name]) {
            this.channels[name] = new Channel(this.webSocket, name, this.options);
        }
        return this.channels[name];
    }
    /**
     * Create a fresh connection.
     */
    connect() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.webSocket) {
                return (this.connected = false);
            }
            yield ((_a = this.webSocket) === null || _a === void 0 ? void 0 : _a.connect());
            return (this.connected = true);
        });
    }
    /**
     * Disconnect webocket connection.
     */
    disconnect() {
        this.webSocket.close();
        this.connected = false;
    }
    /**
     * Leave the given channel, as well as its private and presence variants.
     */
    leave(name) {
        let channels = [name, "private-" + name, "presence-" + name];
        channels.forEach((name) => this.leaveChannel(name));
    }
    /**
     * Leave the given channel.
     */
    leaveChannel(name) {
        if (this.channels[name]) {
            this.webSocket.unsubscribe(name);
            delete this.channels[name];
        }
    }
    /**
     * Listen for an event on a channel instance.
     */
    listen(name, event, callback) {
        return this.channel(name).listen(event, callback);
    }
    /**
     * Get a presence channel instance by name.
     */
    presenceChannel(name) {
        if (!this.channels["presence-" + name]) {
            this.channels["presence-" + name] = new PresenceChannel(this.webSocket, "presence-" + name, this.options);
        }
        return this.channels["presence-" + name];
    }
    /**
     * Get a private channel instance by name.
     */
    privateChannel(name) {
        if (!this.channels["private-" + name]) {
            this.channels["private-" + name] = new PrivateChannel(this.webSocket, "private-" + name, this.options);
        }
        return this.channels["private-" + name];
    }
    /**
     * Get the socket ID for the connection.
     */
    socketId() {
        return this.webSocket.connectionId;
    }
}

class EchoWebSocket {
    /**
     * Create a new instance of the WebSocket Interface
     */
    constructor(url, options = {}) {
        this.url = url;
        this.options = options;
        /**
         * The default options of the socket connection.
         */
        this.defaultOptions = {
            retryConnection: true,
            retryCount: 10,
            retryDelay: 10000,
        };
        /**
         * The channels subjects.
         */
        this.channelCallbacks = {};
        /**
         * The channels subjects.
         */
        this.channelSubjects = {};
        /**
         * The received subject.
         */
        this.received = new Subject();
        /**
         * The subscriptions of the socket.
         */
        this.subscriptions = {};
        this.options = Object.assign(this.defaultOptions, this.options);
    }
    /**
     * Retrieve a signature for a subscription to a private channel.
     */
    authenticatePrivateChannel(channelName) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.options.authEndpoint) {
                throw new Error("An auth endpoint is required to authenticate private channels.");
            }
            return new Promise((resolve, reject) => {
                var _a, _b, _c, _d;
                const request = new XMLHttpRequest();
                request.onload = (e) => {
                    var _a;
                    resolve((_a = request.response) === null || _a === void 0 ? void 0 : _a.auth);
                };
                request.onerror = (e) => {
                    reject(e);
                };
                request.open("POST", this.options.authEndpoint);
                request.responseType = "json";
                request.setRequestHeader("Content-Type", "application/json");
                if ((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.auth) === null || _b === void 0 ? void 0 : _b.headers) {
                    Object.entries(this.options.auth.headers).forEach(([key, value]) => {
                        request.setRequestHeader(key, value);
                    });
                }
                if ((_d = (_c = this.options) === null || _c === void 0 ? void 0 : _c.auth) === null || _d === void 0 ? void 0 : _d.withCredentials) {
                    request.withCredentials = true;
                }
                request.send(JSON.stringify({
                    channel_name: channelName,
                    connection_id: this.connectionId,
                }));
            });
        });
    }
    /**
     * Broadcast data to the subject.
     */
    broadcast(data) {
        this.webSocketSubject.next(data);
    }
    /**
     * Close the WebSocket connection.
     */
    close() {
        if (this.webSocketSubject) {
            this.webSocketSubject.complete();
            this.connected = false;
        }
    }
    /**
     * Create the websocket connection.
     */
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connected) {
                return;
            }
            if (this.connecting) {
                return this.connecting;
            }
            return (this.connecting = new Promise((resolve) => {
                this.createWebSocketSubject()
                    .pipe(retryWhen((errors) => errors.pipe(delay(this.options.retryDelay), take(this.options.retryConnection ? this.options.retryCount : 0))))
                    .subscribe({
                    next: (message) => {
                        this.onConnected(message, resolve);
                        this.received.next(message);
                    },
                });
            }));
        });
    }
    /**
     * Create a Laravel Echo instance.
     */
    /**
     * Create a Laravel Echo connector instance.
     */
    static connector(url, options = {}) {
        return class {
            constructor() {
                return new WebSocketConnector(new EchoWebSocket(url, options), options);
            }
        };
    }
    /**
     * Create the WebSocket subject with rxjs.
     */
    createWebSocketSubject() {
        return (this.webSocketSubject = webSocket(this.url));
    }
    /**
     * Check if the channel name is a private channel.
     */
    isGuarded(channelName) {
        return (channelName.startsWith("private-") || channelName.startsWith("presence-"));
    }
    /**
     * Handle a channel subscription being closed.
     */
    onClose(e) {
        //
    }
    /**
     * On connected handler.
     */
    onConnected(message, resolver) {
        if (message.status === "connected") {
            this.connectionId = message.connection_id;
            this.connected = true;
            resolver();
        }
    }
    /**
     * Stop listening for an event.
     */
    off(channel, event) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.channelCallbacks[channel]) {
                delete this.channelCallbacks[channel][event];
            }
        });
    }
    /**
     * Start listening for an event.
     */
    on(channel, event, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.subscribe(channel);
            if (this.channelCallbacks[channel]) {
                this.channelCallbacks[channel][event] = callback;
            }
        });
    }
    /**
     * Send a message through the socket.
     */
    send(channelName, message) {
        this.broadcast({
            action: "message",
            body: message,
            channel: channelName,
        });
    }
    /**
     * Subscribe the socket to a channel.
     */
    subscribe(channelName, success = null, error = null) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            yield this.connect();
            let auth;
            if (this.subscriptions[channelName] &&
                ((_a = this.subscriptions[channelName]) === null || _a === void 0 ? void 0 : _a.closed) !== true) {
                return;
            }
            if (this.isGuarded(channelName)) {
                try {
                    auth = yield this.authenticatePrivateChannel(channelName);
                    if (!auth) {
                        return;
                    }
                }
                catch (error) {
                    throw error;
                }
            }
            if (!this.channelCallbacks[channelName]) {
                this.channelCallbacks[channelName] = {};
            }
            if (success) {
                this.channelCallbacks[channelName]["*"] = success;
            }
            this.broadcast({
                channel: channelName,
                action: "subscribe",
                auth: auth,
            });
            this.subscriptions[channelName] = this.received
                .pipe(filter((m) => m.channel === channelName))
                .subscribe((message) => {
                Object.keys(this.channelCallbacks[channelName]).forEach((key) => this.channelCallbacks[channelName][key](message));
            });
        });
    }
    /**
     * Unsubscibe the socket from a channel.
     */
    unsubscribe(channelName) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            Object.entries(this.subscriptions).forEach((sub, key) => {
                // sub.events.forEach(e => sub.off(e))
                // delete this.subscriptions[key];
            });
            this.broadcast({
                channel: channelName,
                action: "unsubscribe",
            });
            (_a = this.subscriptions[channelName]) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        });
    }
}

export { EchoWebSocket };
